import React, { useEffect, useMemo, useState } from 'react';
import { Check, Plus, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../../../../../../../../components/ui/button';
import { Card, CardContent } from '../../../../../../../../components/ui/card';
import { cn } from '../../../../../../../../lib/utils';
import { formatLabel, StatusBadge } from '../../pipeMillReportUtils';
import {
  AdjudicationPanel,
  BendDispositionPanel,
  ChemicalFormulaRow,
  ChemistryResultRow,
  FlatteningDispositionPanel,
  HydrostaticDispositionPanel,
  ItemActionButtons,
  KeyValueRows,
  MiniStatusDot,
  PipeLocationDiagram,
  ReportMetadataRail,
  ResultRow,
  SectionHead,
  StatCard,
  TensileResultRow,
} from './shared/components';
import {
  AUT_DETAIL,
  BEND_DETAIL,
  CHEMICAL_DETAIL,
  CVN_DETAIL,
  DIMENSIONAL_DETAIL,
  HARDNESS_DETAIL,
  HYDROSTATIC_DETAIL,
  STANDARD_REPORT_DETAILS,
} from './shared/sampleData';
import {
  average,
  formatDisplayDate,
  getStandardReportSummaryStats,
  standardLimitText,
  standardScaleMax,
  standardScaleMin,
  statusNoteClassName,
} from './shared/format';
import {
  createSampleMeasurement,
  isSampleDataDeleted,
} from './shared/measurements';
import {
  getProjectLimitField,
  getProjectLimitMax,
  getProjectLimitMin,
  getProjectLimitRows,
  getProjectLimitText,
} from './shared/limits';
import type {
  ChemicalElementDisplay,
  FullReportType,
  ProjectLimitFieldOption,
  ReportDefinition,
  ReportMeasurementItem,
  ReportMetadataEntry,
  ReportSummary,
  StandardCheck,
} from './shared/types';
import type { BaseReportDetailProps } from './shared/reportProps';

export const FlatteningReportDetail: React.FC<{
  heatNumber: string;
  metadata?: ReportMetadataEntry;
  projectLimitFields?: ProjectLimitFieldOption[];
  frequency?: string;
  measurements?: ReportMeasurementItem[];
  deletedMeasurementIds?: string[];
  onAddMeasurement?: (reportType: FullReportType) => void;
  onEditMeasurement?: (measurement: ReportMeasurementItem) => void;
  onDeleteMeasurement?: (measurement: ReportMeasurementItem) => void;
}> = ({ heatNumber, metadata, projectLimitFields = [], frequency, measurements = [], deletedMeasurementIds = [], onAddMeasurement, onEditMeasurement, onDeleteMeasurement }) => {
  const flatteningMeasurements = measurements.filter((measurement) => measurement.reportType === 'FLATTENING');
  const effectiveFrequency = metadata?.frequency || frequency || 'per_lot';
  const flatteningHeightLimitField = getProjectLimitField(projectLimitFields, 'flattening_height_h');
  const flatteningHeightLimit = flatteningHeightLimitField?.max ?? flatteningHeightLimitField?.min ?? 0.85;
  const projectLimitRows = getProjectLimitRows(projectLimitFields, [
    ['No project limits', 'Configure project limits to show live values', true],
  ]);
  const specimens = [
    { id: 'F1', weldPosition: '0deg', odBeforeMm: 660.4, hdReached: 0.85, limit: 0.85, cracksObserved: false, status: 'PASS' },
    { id: 'F2', weldPosition: '90deg', odBeforeMm: 660.4, hdReached: 0.72, limit: 0.75, cracksObserved: false, status: 'PASS' },
  ].filter((item) => !isSampleDataDeleted(deletedMeasurementIds, `flattening-${item.id}`)).map((item) => ({
    ...item,
    limit: flatteningHeightLimit,
    measurement: createSampleMeasurement('FLATTENING', `flattening-${item.id}`, 'Flattening specimen', item.hdReached.toFixed(2), 'H/D', 'H/D <= limit; no cracks', item.status, {
      fieldPath: 'flattening_specimen',
      flatteningSpecimenId: item.id,
      flatteningWeldPosition: item.weldPosition,
      flatteningOdBeforeMm: item.odBeforeMm.toFixed(1),
      flatteningHdReached: item.hdReached.toFixed(2),
      flatteningLimit: item.limit.toFixed(2),
      flatteningCracksObserved: item.cracksObserved ? 'yes' : 'no',
    }),
  })).concat([
    ...flatteningMeasurements.map((measurement) => {
      const hdReached = Number(measurement.flatteningHdReached) || 0;
      const limit = flatteningHeightLimit;
      const cracksObserved = measurement.flatteningCracksObserved === 'yes';
      return {
        id: measurement.flatteningSpecimenId || measurement.id,
        weldPosition: measurement.flatteningWeldPosition || '0deg',
        odBeforeMm: Number(measurement.flatteningOdBeforeMm) || 660.4,
        hdReached,
        limit,
        cracksObserved,
        status: measurement.status,
        measurement,
      };
    }),
  ]);

  const renderFlattenCard = (specimen: typeof specimens[number]) => {
    const h = Math.max(0.35, Math.min(1, specimen.hdReached));
    return (
      <button
        key={specimen.id}
        type="button"
        onClick={() => specimen.measurement && onEditMeasurement?.(specimen.measurement)}
        className={cn('rounded-lg border border-[#E2E5EA] bg-white p-4 text-left shadow-sm', specimen.measurement ? 'cursor-pointer hover:border-hero-red-20 hover:shadow-md' : 'cursor-default')}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Specimen {specimen.id}</div>
            <div className="mt-1 text-[12px] font-semibold text-blueprint-blue">Weld at {specimen.weldPosition}</div>
          </div>
          <StatusBadge status={specimen.status} />
        </div>
        <svg viewBox="0 0 220 140" className="mt-3 h-[140px] w-full">
          <rect x="20" y="20" width="180" height="6" fill="#464646" />
          <rect x="20" y={20 + 90 * (1 - h) + 14} width="180" height="6" fill="#464646" />
          <ellipse cx="110" cy="70" rx="80" ry={45 * h} fill="#EEF0F3" stroke="#969696" strokeWidth="2" />
          <ellipse cx="110" cy="70" rx="72" ry={Math.max(8, 38 * h)} fill="#FFFFFF" stroke="#C7CCD5" strokeWidth="1.5" />
          {specimen.weldPosition === '0deg' ? (
            <circle cx="190" cy="70" r="5" fill="#00273F" />
          ) : (
            <circle cx="110" cy={70 - 45 * h} r="5" fill="#00273F" />
          )}
        </svg>
        <div className="mt-2 flex items-end justify-between gap-3">
          <div>
            <div className="text-[10px] text-heritage-grey">H/D reached</div>
            <div className="flex items-baseline gap-1">
              <span className="font-heading text-[22px] font-semibold text-blueprint-blue">{specimen.hdReached.toFixed(2)}</span>
              <span className="font-mono text-[10px] text-heritage-grey">&lt;= {specimen.limit.toFixed(2)}</span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] text-heritage-grey">Cracks</div>
            <div className={cn('font-mono text-[13px] font-semibold', specimen.cracksObserved ? 'text-readable-red' : 'text-galvanizing-green')}>
              {specimen.cracksObserved ? 'Yes' : 'None'}
            </div>
          </div>
        </div>
      </button>
    );
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px]">
      <main className="space-y-5 p-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h3 className="font-heading text-[16px] font-semibold text-blueprint-blue">Two specimens - weld at 0deg and 90deg</h3>
            <div className="mt-1 text-[12px] text-heritage-grey">Ring is compressed flat - weld must reach H/D limit without cracking</div>
          </div>
          <Button type="button" size="sm" onClick={() => onAddMeasurement?.('FLATTENING')} className="bg-hero-red text-white hover:bg-hero-red-60">
            <Plus className="mr-2 h-4 w-4" />
            Add Specimen
          </Button>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {specimens.map(renderFlattenCard)}
        </div>

        <Card className="overflow-hidden border-[#E2E5EA]">
          <CardContent className="p-0">
            <SectionHead title="Results detail" />
            <table className="w-full text-left">
              <thead className="border-b border-[#E2E5EA] bg-page-bg text-[10px] font-bold uppercase tracking-wider text-heritage-grey">
                <tr>
                  <th className="px-4 py-3">Specimen</th>
                  <th className="px-4 py-3">Weld position</th>
                  <th className="px-4 py-3 text-right">OD before</th>
                  <th className="px-4 py-3 text-right">H/D reached</th>
                  <th className="px-4 py-3">Cracks</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#EEF0F3] text-[12px] text-text-grey">
                {specimens.map((item) => (
                  <tr key={item.id} onClick={() => item.measurement && onEditMeasurement?.(item.measurement)} className={cn(item.measurement && 'cursor-pointer hover:bg-page-bg')}>
                    <td className="px-4 py-3 font-mono font-semibold">{item.id}</td>
                    <td className="px-4 py-3">{item.weldPosition}</td>
                    <td className="px-4 py-3 text-right font-mono">{item.odBeforeMm.toFixed(1)} mm</td>
                    <td className="px-4 py-3 text-right font-mono">{item.hdReached.toFixed(2)}</td>
                    <td className="px-4 py-3">{item.cracksObserved ? 'Yes' : 'No'}</td>
                    <td className="px-4 py-3"><StatusBadge status={item.status} /></td>
                    <td className="px-4 py-3 text-right">
                      {item.measurement && (
                        <ItemActionButtons measurement={item.measurement} onEditMeasurement={onEditMeasurement} onDeleteMeasurement={onDeleteMeasurement} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </main>

      <ReportMetadataRail
        rows={[
          ['Report ID', metadata?.reportId || '-', true],
          ['Performed at', formatDisplayDate(metadata?.performedAt), true],
          ['Heat', heatNumber, true],
          ['Lab', metadata?.lab || '-'],
          ['Technician', metadata?.technician || '-'],
          ['Source', metadata?.source ? formatLabel(metadata.source) : '-'],
          ['Source file', metadata?.sourceFile || '-', true],
        ]}
        extraTitle="Limits In Effect"
        extraRows={projectLimitRows}
      >
        <div className="mt-5 text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Frequency / Scope</div>
        <Card className="mt-3 border-[#E2E5EA]">
          <CardContent className="p-4">
            <KeyValueRows
              rows={[
                ['Frequency', effectiveFrequency, true],
                ['Heat number', heatNumber || '-', true],
                ['Specimens', `${flatteningMeasurements.length} saved`, true],
              ]}
            />
          </CardContent>
        </Card>
        <FlatteningDispositionPanel />
        <AdjudicationPanel />
      </ReportMetadataRail>
    </div>
  );
};
